import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Clock, CheckCircle, User, MessageSquare, Paperclip, 
  Briefcase, Calendar, AlertCircle, Eye
} from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';
import TaskAttachments from '../components/TaskAttachments';

const TareasEnRevision = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [tareasEnRevision, setTareasEnRevision] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal para ver bitácora
  const [showBitacora, setShowBitacora] = useState(false);
  const [tareaSeleccionada, setTareaSeleccionada] = useState(null);
  
  // Modal para ver documentos
  const [showDocs, setShowDocs] = useState(false);
  const [tareaDocs, setTareaDocs] = useState(null);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchTareasEnRevision();
    }
  }, [currentUser]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error al cargar usuario:', error);
    }
  };

  const fetchTareasEnRevision = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tasks/mis-tareas-en-revision');
      setTareasEnRevision(response.data || []);
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'En Revisión': return 'bg-amber-100 text-amber-700';
      case 'Revisión Socios': return 'bg-purple-100 text-purple-700';
      case 'Completada': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getTotalAdjuntos = (tarea) => {
    let total = (tarea.attachments || []).length;
    (tarea.gestiones || []).forEach(g => {
      total += (g.attachments || []).length;
    });
    return total;
  };

  const handleVerBitacora = (tarea) => {
    setTareaSeleccionada(tarea);
    setShowBitacora(true);
  };

  const handleVerDocs = (tarea) => {
    setTareaDocs(tarea);
    setShowDocs(true);
  };

  return (
    <div className="flex h-screen bg-white">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      
      <main className={`flex-1 transition-all duration-300 ${collapsed ? 'ml-16' : 'ml-64'} overflow-y-auto`}>
        <div className="p-8 max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 mb-8"
          >
            <div className="p-3 bg-amber-100 rounded-xl">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Tareas en Revisión</h1>
              <p className="text-slate-600 mt-1">
                Tareas de tu flujo de trabajo que están pendientes de aprobación
              </p>
            </div>
          </motion.div>

          {/* Card informativa */}
          <Card className="p-4 border-amber-200 bg-amber-50/50 mb-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <p className="text-sm text-amber-700">
                Aquí puedes ver las tareas que has completado y están esperando revisión de un Líder o Socio,
                así como las tareas de tu área que están en proceso de aprobación.
              </p>
            </div>
          </Card>

          {/* Lista de tareas */}
          {loading ? (
            <Card className="p-12 text-center border-slate-200">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-600">Cargando tareas en revisión...</p>
            </Card>
          ) : tareasEnRevision.length === 0 ? (
            <Card className="p-12 text-center border-slate-200">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-lg text-slate-600">No tienes tareas en proceso de revisión</p>
              <p className="text-sm text-slate-500 mt-2">
                Las tareas aparecerán aquí cuando las marques como completadas y estén esperando aprobación
              </p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {tareasEnRevision.map((tarea) => (
                <motion.div
                  key={tarea.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="p-5 border-amber-200 hover:shadow-md transition-shadow" data-testid={`tarea-revision-${tarea.id}`}>
                    <div className="flex items-start justify-between gap-6">
                      {/* Info principal */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-slate-800 text-lg">{tarea.title}</h3>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(tarea.status)}`}>
                            {tarea.status}
                          </span>
                        </div>
                        
                        <p className="text-sm text-slate-600 line-clamp-2 mb-4">{tarea.description}</p>
                        
                        {/* Metadatos en grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          {/* Cliente */}
                          {tarea.client_name && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                              <Briefcase className="w-4 h-4 text-blue-600" />
                              <span className="text-blue-700 font-medium truncate">{tarea.client_name}</span>
                            </div>
                          )}
                          
                          {/* PSP */}
                          {tarea.psp_folio && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg">
                              <span className="text-purple-700">PSP: {tarea.psp_folio}</span>
                            </div>
                          )}
                          
                          {/* Responsable */}
                          {tarea.responsible && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                              <User className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-600 truncate">{tarea.responsible}</span>
                            </div>
                          )}
                          
                          {/* Área */}
                          {tarea.area && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                              <span className="text-slate-600">{tarea.area}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Panel derecho: Revisor y Fecha */}
                      <div className="flex-shrink-0 min-w-[200px]">
                        {/* Revisor Actual */}
                        <div className={`p-4 rounded-lg mb-3 ${
                          tarea.nivel_revision === 'Directivo' 
                            ? 'bg-purple-50 border border-purple-200' 
                            : 'bg-blue-50 border border-blue-200'
                        }`}>
                          <p className="text-xs text-slate-500 mb-1">Revisor Actual</p>
                          <p className={`font-bold text-base ${
                            tarea.nivel_revision === 'Directivo' ? 'text-purple-700' : 'text-blue-700'
                          }`}>
                            {tarea.revisor_actual || 'Por asignar'}
                          </p>
                          <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs ${
                            tarea.nivel_revision === 'Directivo' 
                              ? 'bg-purple-200 text-purple-700' 
                              : 'bg-blue-200 text-blue-700'
                          }`}>
                            Nivel: {tarea.nivel_revision || 'Líder'}
                          </span>
                        </div>
                        
                        {/* Fecha de envío */}
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-600 mb-1 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Enviada a revisión
                          </p>
                          <p className="font-semibold text-amber-800 text-sm">
                            {tarea.fecha_envio_revision 
                              ? new Date(tarea.fecha_envio_revision).toLocaleDateString('es-CL', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'Sin fecha'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-slate-100">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-slate-600 hover:text-slate-800"
                        onClick={() => handleVerBitacora(tarea)}
                        data-testid={`ver-bitacora-${tarea.id}`}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Ver Bitácora
                      </Button>
                      
                      {getTotalAdjuntos(tarea) > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-green-600 hover:text-green-700 border-green-200"
                          onClick={() => handleVerDocs(tarea)}
                          data-testid={`ver-docs-${tarea.id}`}
                        >
                          <Paperclip className="w-4 h-4 mr-2" />
                          {getTotalAdjuntos(tarea)} Documentos
                        </Button>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal Ver Bitácora */}
      <Dialog open={showBitacora} onOpenChange={setShowBitacora}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3649] text-lg font-semibold">
              <MessageSquare className="w-5 h-5" />
              Bitácora de la Tarea
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Historial completo de gestiones y comentarios
            </DialogDescription>
          </DialogHeader>
          {tareaSeleccionada && (
            <div className="mt-4">
              {/* Detalle de la tarea */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-slate-800">{tareaSeleccionada.title}</p>
                <p className="text-xs text-slate-600 mt-1">{tareaSeleccionada.description}</p>
                <div className="flex items-center gap-3 mt-2 text-xs">
                  <span className={`px-2 py-0.5 rounded ${getStatusColor(tareaSeleccionada.status)}`}>
                    {tareaSeleccionada.status}
                  </span>
                  {tareaSeleccionada.responsible && (
                    <span className="text-slate-600">Responsable: {tareaSeleccionada.responsible}</span>
                  )}
                </div>
              </div>

              {/* Bitácora de Gestiones */}
              <div className="space-y-3">
                {(!tareaSeleccionada.gestiones || tareaSeleccionada.gestiones.length === 0) ? (
                  <p className="text-sm text-slate-500 text-center py-8">No hay gestiones registradas en esta tarea</p>
                ) : (
                  tareaSeleccionada.gestiones.map((gestion, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${
                      gestion.tipo === 'completada' ? 'bg-blue-50 border-blue-200' :
                      gestion.tipo === 'aprobacion' ? 'bg-green-50 border-green-200' :
                      gestion.tipo === 'rechazo' ? 'bg-red-50 border-red-200' :
                      gestion.tipo === 'revision_socios' ? 'bg-amber-50 border-amber-200' :
                      'bg-white border-slate-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-slate-800">{gestion.usuario}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            gestion.tipo === 'completada' ? 'bg-blue-200 text-blue-800' :
                            gestion.tipo === 'aprobacion' ? 'bg-green-200 text-green-800' :
                            gestion.tipo === 'rechazo' ? 'bg-red-200 text-red-800' :
                            gestion.tipo === 'revision_socios' ? 'bg-amber-200 text-amber-800' :
                            'bg-slate-200 text-slate-800'
                          }`}>
                            {gestion.tipo || 'Gestión'}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {new Date(gestion.fecha).toLocaleString('es-CL')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">{gestion.descripcion}</p>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowBitacora(false)} className="px-6">
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Ver Documentos */}
      <Dialog open={showDocs} onOpenChange={setShowDocs}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3649] text-lg font-semibold">
              <Paperclip className="w-5 h-5" />
              Documentos de la Tarea
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Archivos adjuntados durante el proceso
            </DialogDescription>
          </DialogHeader>
          {tareaDocs && (
            <div className="mt-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-slate-800">{tareaDocs.title}</p>
              </div>
              
              <TaskAttachments
                entityType="task"
                entityId={tareaDocs.id}
                attachments={tareaDocs.attachments || []}
                canUpload={false}
                showBitacoraAttachments={true}
              />
              
              <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowDocs(false)} className="px-6">
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TareasEnRevision;
